/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Avatar,
  styled,
  Link as MuiLink,
  createTheme,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Logout,
  Search,
  GetApp,
  Refresh,
  FilterList,
  Visibility,
  Close,
  School as SchoolIcon,
  Close as CloseIcon,
  Person,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import { getUser, baseUrl, requestID, config, logOutUser } from "../../common/utils";
import axios from "axios";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching the image
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

// Stats Cards styled to match image
const StatsGrid = styled(Grid)({
  marginBottom: "32px",
});

const StatCard = styled(Paper)({
  padding: "20px 24px",
  height: "100%",
  backgroundColor: "#ffffff",
});

const StatLabel = styled(Typography)({
  fontSize: "14px",
  color: "#6b7280",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontWeight: 500,
});

const StatValue = styled(Typography)({
  fontSize: "32px",
  fontWeight: 700,
  color: "#1f2937",
  lineHeight: 1.2,
});

const GenderBox = styled(Box)({
  display: "flex",
  gap: "16px",
  marginTop: "4px",
});

const GenderItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const GenderNumber = styled(Typography)({
  fontSize: "20px",
  fontWeight: 600,
});

const GenderLabel = styled(Typography)({
  fontSize: "14px",
  color: "#6b7280",
});

const StatusBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const StatusActive = styled(Typography)({
  fontSize: "20px",
  fontWeight: 600,
  color: "#10b981",
  lineHeight: 1.2,
});

const StatusOnline = styled(Typography)({
  fontSize: "14px",
  color: "#6b7280",
});

// Filter bar styled to match image
const FilterBar = styled(Paper)({
  padding: "16px 20px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
});

const SearchField = styled(TextField)({
  flex: 2,
  minWidth: "300px",
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
  },
});

const FilterSelect = styled(FormControl)({
  minWidth: '120px',
  backgroundColor: '#ffffff',
});

const ActionButton = styled(Button)({
  border: "1px solid #e5e7eb",
  color: "#374151",
  backgroundColor: "#ffffff",
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: "#f9fafb",
    borderColor: "#d1d5db",
  },
});

const IconActionButton = styled(IconButton)({
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "8px",
  backgroundColor: "#ffffff",
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

// Table styles to match image
const StyledTableContainer = styled(TableContainer)({
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "16px",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#f8f9fa",
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: "13px",
    color: "#4b5563",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "14px",
  borderBottom: "1px solid #e5e7eb",
});

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

const ViewButton = styled(Button)({
  backgroundColor: "#243b5a",
  color: "white",
  padding: "4px 12px",
  fontSize: "12px",
  minWidth: "60px",
  "&:hover": {
    backgroundColor: "#162c47",
  },
  "& svg": {
    fontSize: "16px",
    marginRight: "4px",
  },
});

const StudentViewButton = styled(IconButton)({
  backgroundColor: "#e3f2fd",
  padding: "6px",
  "&:hover": {
    backgroundColor: "#bbdefb",
  },
  "& svg": {
    fontSize: "20px",
    color: "#243b5a",
  },
});

// Types based on API responses
interface SchoolSummary {
  id: number;
  schoolName: string;
  totalStudents: number;
  totalMale: number;
  totalFemale: number;
}

interface SchoolsSummaryResponse {
  totalSchools: number;
  grandTotalStudents: number;
  grandTotalMale: number;
  grandTotalFemale: number;
  schools: SchoolSummary[];
}

interface Student {
  id: string;
  studentNumber: string;
  firstname: string;
  lastname: string;
  othername: string;
  gender: string;
  schoolId: string;
  schoolType: string;
  dateOfBirth: string;
  passport: string | null;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  lgacode: string;
  year: string;
  accCode: string;
  class?: string;
}

interface SchoolDetailsResponse {
  schoolName: string;
  schoolCode: string;
  lgaCode: string;
  totalStudents: number;
  totalMale: number;
  totalFemale: number;
  students: Student[];
}

// ============================================
// Main ValidationListPage Component
// ============================================
const ValidationListPage: React.FC = () => {
  const history = useHistory();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Get user data from utils
  const user = getUser();
  
  const userSchoolData = {
    schoolName: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  // State for schools summary
  const [schoolsData, setSchoolsData] = useState<SchoolsSummaryResponse | null>(null);
  const [schools, setSchools] = useState<SchoolSummary[]>([]);
  
  // State for school details
  const [selectedSchoolDetails, setSelectedSchoolDetails] = useState<SchoolDetailsResponse | null>(null);
  const [schoolStudents, setSchoolStudents] = useState<Student[]>([]);
  
  // State for student details
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentDetailsOpen, setStudentDetailsOpen] = useState(false);
  
  // UI State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | '', text: string }>({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("All Year");
  const [schoolFilter, setSchoolFilter] = useState("All Schools");
  const [studentFilter, setStudentFilter] = useState("All Students");

  // Add these state variables for student filtering
const [studentSearchTerm, setStudentSearchTerm] = useState("");
const [genderFilter, setGenderFilter] = useState("All");
const [schoolTypeFilter, setSchoolTypeFilter] = useState("All");

  // Fetch schools summary on page load
  const fetchSchoolsSummary = async () => {
    setLoading(true);
    try {
      if (!user?.lgaCode) {
        setMessage({ type: "error", text: "LGA code not found" });
        return;
      }

      const response = await axios.post(
        `${baseUrl()}cies/getSchoolsSummaryByLga`,
        {
          lgaCode: user.lgaCode,
          requestID: requestID(),
        },
        config
      );
      console.log(response.data);

      if (response.data.status) {
        setSchoolsData(response.data.data);
        setSchools(response.data.data.schools || []);
        setMessage({ type: "success", text: "Schools data loaded successfully" });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: "error", text: response.data.message || "Failed to fetch schools data" });
      }
    } catch (error) {
      console.error("Error fetching schools summary:", error);
      setMessage({ type: "error", text: "Error connecting to server" });
    } finally {
      setLoading(false);
    }
  };

  // Fetch school details when a school is selected
  const fetchSchoolDetails = async (schoolId: number) => {
    setDetailsLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl()}cies/getSchoolDetails`,
        {
          schoolId: schoolId,
          requestID: requestID(),
        },
        config
      );
      console.log(response.data);

      if (response.data.status) {
        setSelectedSchoolDetails(response.data.data);
        setSchoolStudents(response.data.data.students || []);
        setDialogOpen(true);
      } else {
        setMessage({ type: "error", text: response.data.message || "Failed to fetch school details" });
      }
    } catch (error) {
      console.error("Error fetching school details:", error);
      setMessage({ type: "error", text: "Error connecting to server" });
    } finally {
      setDetailsLoading(false);
    }
  };

  // Handle view student details
  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setStudentDetailsOpen(true);
  };

  // Handle close student details
  const handleCloseStudentDetails = () => {
    setStudentDetailsOpen(false);
    setSelectedStudent(null);
  };

  // Initial fetch
  useEffect(() => {
    fetchSchoolsSummary();
  }, []);

  // Handle view school details
  const handleViewSchool = (school: SchoolSummary) => {
    fetchSchoolDetails(school.id);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSchoolDetails(null);
    setSchoolStudents([]);
  };

  // Handle logout
  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    history.push("/school_dashboard");
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchSchoolsSummary();
  };

  // Handle export CSV
  const handleExportCSV = () => {
    console.log("Exporting CSV...");
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setYearFilter("All Year");
    setSchoolFilter("All Schools");
    setStudentFilter("All Students");
  };

  const getGenderColor = (gender: string) => {
    return gender.toLowerCase() === "male" ? "#2196f3" : "#e91e63";
  };

  // Filter schools based on search
  const filteredSchools = schools.filter(school => {
    const matchesSearch = school.schoolName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Filter students based on search and filters
const filteredStudents = schoolStudents.filter(student => {
  // Search filter
  const matchesSearch = studentSearchTerm === "" || 
    `${student.lastname} ${student.firstname} ${student.othername}`.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.studentNumber.toLowerCase().includes(studentSearchTerm.toLowerCase());
  
  // Gender filter
  const matchesGender = genderFilter === "All" || student.gender === genderFilter;
  
  // School type filter
  const matchesSchoolType = schoolTypeFilter === "All" || student.schoolType === schoolTypeFilter;
  
  return matchesSearch && matchesGender && matchesSchoolType;
});

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER */}
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      School Registration Summary
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userSchoolData.schoolName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userSchoolData.lgaCode} | Code: {userSchoolData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>
            </Header>

            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              {/* Back to Dashboard Button */}
              <Button
                onClick={handleBackToDashboard}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back to Dashboard
              </Button>

              {/* Dashboard Overview Text */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Monitoring student registration data and performance metrics for 1.
              </Typography>

              {/* Stats Cards - Exactly matching image */}
              <StatsGrid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard>
                    <StatLabel>TOTAL ENROLLED</StatLabel>
                    <StatValue>{schoolsData?.grandTotalStudents?.toLocaleString() || '0'}</StatValue>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard>
                    <StatLabel>GENDER DISTRIBUTION</StatLabel>
                    <GenderBox>
                      <GenderItem>
                        <GenderNumber sx={{ color: "#2196f3" }}>{schoolsData?.grandTotalMale || '0'}</GenderNumber>
                        <GenderLabel>Male</GenderLabel>
                      </GenderItem>
                      <GenderItem>
                        <GenderNumber sx={{ color: "#e91e63" }}>{schoolsData?.grandTotalFemale || '0'}</GenderNumber>
                        <GenderLabel>Female</GenderLabel>
                      </GenderItem>
                    </GenderBox>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard>
                    <StatLabel>LATE REGISTRATIONS</StatLabel>
                    <StatValue>0</StatValue>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard>
                    <StatLabel>PORTAL STATUS</StatLabel>
                    <StatusBox>
                      <StatusActive>Active</StatusActive>
                      <StatusOnline>Online</StatusOnline>
                    </StatusBox>
                  </StatCard>
                </Grid>
              </StatsGrid>

              {/* Message Alert */}
              {message.text && (
                <Alert 
                  severity={message.type === 'success' ? 'success' : 'error'} 
                  sx={{ mb: 3 }}
                  onClose={() => setMessage({ type: '', text: '' })}
                >
                  {message.text}
                </Alert>
              )}

              {/* Search and Filter Bar - Exactly matching image */}
              <FilterBar>
                <SearchField
                  size="small"
                  placeholder="Search by school name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <FilterSelect size="small">
                  <Select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="All Year">All Year</MenuItem>
                  </Select>
                </FilterSelect>

                <FilterSelect size="small">
                  <Select
                    value={schoolFilter}
                    onChange={(e) => setSchoolFilter(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="All Schools">All Schools</MenuItem>
                  </Select>
                </FilterSelect>

                <FilterSelect size="small">
                  <Select
                    value={studentFilter}
                    onChange={(e) => setStudentFilter(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="All Students">All Students</MenuItem>
                  </Select>
                </FilterSelect>

                <ActionButton
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<FilterList />}
                >
                  Clear Filters
                </ActionButton>

                <ActionButton
                  size="small"
                  onClick={handleExportCSV}
                  startIcon={<GetApp />}
                >
                  Export CSV
                </ActionButton>

                <IconActionButton 
                  size="small" 
                  onClick={handleRefresh}
                >
                  <Refresh />
                </IconActionButton>
              </FilterBar>

              {/* Table Header Info */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredSchools.length} of {schools.length} schools
                </Typography>
              </Box>

              {/* Schools Table - Tabular format like the image */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <StyledTableContainer component={Paper}>
                  <Table>
                    <StyledTableHead>
                      <TableRow>
                        <TableCell><b>S/N</b></TableCell>
                        <TableCell><b>SCHOOL NAME</b></TableCell>
                        <TableCell align="center"><b>MALE</b></TableCell>
                        <TableCell align="center"><b>FEMALE</b></TableCell>
                        <TableCell align="center"><b>TOTAL MALE</b></TableCell>
                        <TableCell align="center"><b>TOTAL FEMALE</b></TableCell>
                        <TableCell align="center"><b>TOTAL POPULATION</b></TableCell>
                        <TableCell align="center"><b>ACTION</b></TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {filteredSchools.map((school, index) => (
                        <StyledTableRow key={school.id}>
                          <StyledTableCell>{index + 1}</StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: "#243b5a" }}>
                                <SchoolIcon sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {school.schoolName}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={school.totalMale} 
                              size="small"
                              sx={{ 
                                bgcolor: "#e3f2fd", 
                                color: "#2196f3",
                                fontWeight: 600,
                                minWidth: 50
                              }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip 
                              label={school.totalFemale} 
                              size="small"
                              sx={{ 
                                bgcolor: "#fce4ec", 
                                color: "#e91e63",
                                fontWeight: 600,
                                minWidth: 50
                              }} 
                            />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#2196f3" }}>
                              {school.totalMale}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#e91e63" }}>
                              {school.totalFemale}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {school.totalStudents}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <ViewButton
                              size="small"
                              onClick={() => handleViewSchool(school)}
                              disabled={detailsLoading}
                              startIcon={detailsLoading ? <CircularProgress size={14} /> : <Visibility />}
                            >
                              View
                            </ViewButton>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}

                      {filteredSchools.length === 0 && !loading && (
                        <StyledTableRow>
                          <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No schools found
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination Info */}
                  <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing 1 to {filteredSchools.length} of {schools.length} schools
                    </Typography>
                  </Box>
                </StyledTableContainer>
              )}

              {/* School Students Dialog */}
              {/* School Students Dialog */}
<Dialog
  open={dialogOpen}
  onClose={handleCloseDialog}
  maxWidth="lg"
  fullWidth
  PaperProps={{
    sx: {
      minHeight: "80vh",
      maxHeight: "90vh",
    }
  }}
>
  {selectedSchoolDetails && (
    <>
      <DialogTitle sx={{ 
        bgcolor: "#243b5a", 
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 2
      }}>
        <Box>
          <Typography variant="h6">
            {selectedSchoolDetails.schoolName} - Student List
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Code: {selectedSchoolDetails.schoolCode} | LGA: {selectedSchoolDetails.lgaCode}
          </Typography>
        </Box>
        <IconButton onClick={handleCloseDialog} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Summary Stats for Selected School */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  TOTAL REGISTERED
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#243b5a" }}>
                  {selectedSchoolDetails.totalStudents}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  TOTAL MALE
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#2196f3" }}>
                  {selectedSchoolDetails.totalMale}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  TOTAL FEMALE
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#e91e63" }}>
                  {selectedSchoolDetails.totalFemale}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Search and Filter Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or student number..."
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="All">All Genders</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={schoolTypeFilter}
                  onChange={(e) => setSchoolTypeFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="All">All School Types</MenuItem>
                  <MenuItem value="Public">Public</MenuItem>
                  <MenuItem value="Private">Private</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Tooltip title="Clear filters">
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setStudentSearchTerm("");
                    setGenderFilter("All");
                    setSchoolTypeFilter("All");
                  }}
                  sx={{ border: "1px solid #e5e7eb", borderRadius: "6px", p: 1 }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        {/* Students Table */}
        {detailsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><b>S/N</b></TableCell>
                    <TableCell><b>Photo</b></TableCell>
                    <TableCell><b>Student Number</b></TableCell>
                    <TableCell><b>Student Name</b></TableCell>
                    <TableCell><b>Gender</b></TableCell>
                    <TableCell><b>School Type</b></TableCell>
                    <TableCell><b>Access Code</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell align="center"><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student, index) => (
                      <TableRow key={student.id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Avatar
                            src={student.passport ? `${baseUrl()}/assets/${student.passport}` : undefined}
                            sx={{ width: 40, height: 40 }}
                          >
                            {student.firstname?.[0]}{student.lastname?.[0]}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontFamily: "monospace", fontWeight: 500 }}>
                            {student.studentNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {student.lastname} {student.firstname} {student.othername || ''}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.gender}
                            size="small"
                            sx={{
                              bgcolor: getGenderColor(student.gender),
                              color: "white",
                              minWidth: 60,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.schoolType || 'N/A'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontFamily: "monospace" }}>
                            {student.accCode || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              bgcolor: "#10b981",
                              color: "white",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <StudentViewButton
                            size="small"
                            onClick={() => handleViewStudent(student)}
                            title="View Student Details"
                          >
                            <Visibility />
                          </StudentViewButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No students match your search criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Info */}
            {filteredStudents.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredStudents.length} of {selectedSchoolDetails.totalStudents} students
                  {filteredStudents.length !== schoolStudents.length && ' (filtered)'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => console.log("Export school list")}
                  startIcon={<GetApp />}
                >
                  Export List
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        <Button
          onClick={handleCloseDialog}
          variant="contained"
          sx={{
            bgcolor: "#243b5a",
            "&:hover": { bgcolor: "#162c47" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </>
  )}
</Dialog>

              {/* Student Details Dialog - From the sample component */}
              <Dialog 
                open={studentDetailsOpen} 
                onClose={handleCloseStudentDetails}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle sx={{ 
                  bgcolor: "#243b5a", 
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 2
                }}>
                  <Typography variant="h6">Student Details</Typography>
                  <IconButton onClick={handleCloseStudentDetails} sx={{ color: "white" }}>
                    <Close />
                  </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 3 }}>
                  {selectedStudent && (
                    <Grid container spacing={3}>
                      {/* Photo Section */}
                      <Grid item xs={12} display="flex" justifyContent="center">
                        <Avatar
                          src={selectedStudent.passport ? `${baseUrl()}/assets/${selectedStudent.passport}` : undefined}
                          alt={`${selectedStudent.firstname} ${selectedStudent.lastname}`}
                          sx={{ width: 120, height: 120, border: "3px solid #243b5a" }}
                        >
                          {selectedStudent.firstname?.[0]}{selectedStudent.lastname?.[0]}
                        </Avatar>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider>
                          <Chip label="Personal Information" size="small" />
                        </Divider>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1" fontWeight="500">
                          {selectedStudent.lastname} {selectedStudent.firstname} {selectedStudent.othername}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Student Number</Typography>
                        <Typography variant="body1" fontWeight="500">{selectedStudent.studentNumber}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Access Code</Typography>
                        <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{selectedStudent.accCode}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Gender</Typography>
                        <Chip 
                          label={selectedStudent.gender} 
                          size="small"
                          sx={{ 
                            bgcolor: getGenderColor(selectedStudent.gender), 
                            color: "white",
                            mt: 0.5
                          }} 
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">School Type</Typography>
                        <Chip 
                          label={selectedStudent.schoolType} 
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                        <Typography variant="body1">
                          {selectedStudent.dateOfBirth ? new Date(selectedStudent.dateOfBirth).toLocaleDateString() : "N/A"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }}>
                          <Chip label="Academic Scores" size="small" />
                        </Divider>
                      </Grid>

                      <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                              <TableRow>
                                <TableCell><b>Subject</b></TableCell>
                                <TableCell align="center"><b>Term 1</b></TableCell>
                                <TableCell align="center"><b>Term 2</b></TableCell>
                                <TableCell align="center"><b>Term 3</b></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>English</TableCell>
                                <TableCell align="center">{selectedStudent.englishTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.englishTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.englishTerm3 || "-"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Arithmetic</TableCell>
                                <TableCell align="center">{selectedStudent.arithmeticTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.arithmeticTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.arithmeticTerm3 || "-"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>General Paper</TableCell>
                                <TableCell align="center">{selectedStudent.generalTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.generalTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.generalTerm3 || "-"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Religious ({selectedStudent.religiousType || "N/A"})</TableCell>
                                <TableCell align="center">{selectedStudent.religiousTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.religiousTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.religiousTerm3 || "-"}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  )}
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
                  <Button 
                    onClick={handleCloseStudentDetails} 
                    variant="contained"
                    sx={{ 
                      bgcolor: "#243b5a", 
                      "&:hover": { bgcolor: "#162c47" },
                      textTransform: "none",
                      px: 4
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Container>

            {/* FOOTER */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default ValidationListPage;